import { useDispatch } from 'react-redux';
import { FieldArray, Formik } from 'formik';
import { createLoadHoc } from '../../../../helpers/hocs/createLoadHoc';
import {
    selectGetSiteBlocksLoader,
    selectSiteBodyBlock,
} from '../../../../selectors/mainPage';
import {
    BannerItemEdit,
    BannerItemForEdit,
    BlockItemEdit,
    WebBlockKey,
} from '../../../../types/Models/MainPage/MainPageModel';
import { useTypeSelector } from '../../../../store';
import { PageTitle } from '../../../../components/PageTitle';
import { Breadcrumbs } from '../../../../components/Breadcrumbs';
import {
    editBannerItemsWeb,
    editWebBanners,
} from '../../../../actions/mainPage';
import { siteBodyBreadcrumbs } from '../../../../constants/BreadCrumbs/siteBodyBreadcrumbs';
import { getShotPath } from '../../../../helpers/images/getPath';
import { SubmitButtons } from '../../../../components/StyledComponents';
import { isEqualObject } from '../../../../helpers/isEqual';
import { addNotification } from '../../../../actions/notifications';
import { TypesSnackbars } from '../../../../types/TypesSnackbars';
import { TypesIcon } from '../../../../types/TypesIcon';
import * as S from './styles';
import { validationSchema } from './schema';

export const OurServicesContent = (): JSX.Element => {
    const dispatch = useDispatch();

    const siteBodyBlock = useTypeSelector(
        selectSiteBodyBlock(WebBlockKey.WEB_SERVICES),
    );

    const handleSubmit = (values: any) => {
        const bannerRequestData = {
            buttonText: values.buttonText || null,
            title: values.title || null,
            url: values.url || null,
        };

        const requestData = {
            bannerItems: values.bannerItems.map(
                (item: BannerItemForEdit, index: number) => ({
                    buttonText: item.buttonText || null,
                    title: item.title || null,
                    text: item.text || null,
                    url: item.url || null,
                    fileId: item.fileImage?.id || null,
                    status: item.status || 'INACTIVE',
                    order: item.order || index + 1,
                    endDate: item.endDate || null,
                    startDate: item.startDate || null,
                    mobileFileId: item.mobileFileImage?.id || null,
                }),
            ),
        };

        dispatch(
            editWebBanners({
                key: WebBlockKey.WEB_SERVICES,
                newData: bannerRequestData,
            }),
        );
        dispatch(
            editBannerItemsWeb({
                key: WebBlockKey.WEB_SERVICES,
                newData: requestData.bannerItems,
            }),
        );
    };

    const items = siteBodyBlock?.bannerItems.map(item => ({
        ...item,
        fileImage: {
            id: item?.fileId || 0,
            path: item.fileURL ? getShotPath(item.fileURL) : '',
        },
        mobileFileImage: {
            id: item?.mobileFileId || 0,
            path: item?.mobileFileURL ? getShotPath(item.mobileFileURL) : '',
        },
    }));

    const initialValue: BlockItemEdit<WebBlockKey> = {
        buttonText: siteBodyBlock?.buttonText || '',
        title: siteBodyBlock?.title || '',
        text: siteBodyBlock?.text || '',
        url: siteBodyBlock?.url || '',
        name: siteBodyBlock?.name || '',
        key: WebBlockKey.WEB_SERVICES,
        platform: 'WEB',
        type: 'SLIDER',
        bannerItems: items || [],
        bannerProducts: siteBodyBlock?.bannerProducts || [],
        fileURL: siteBodyBlock?.fileURL || '',
        id: siteBodyBlock?.id || 0,
        fileImage: {
            id: siteBodyBlock?.fileId || 0,
            path: siteBodyBlock?.fileURL ? getShotPath(siteBodyBlock.fileURL) : '',
        },
        mobileFileImage: {
            id: siteBodyBlock?.mobileFileId || 0,
            path: siteBodyBlock?.mobileFileURL
                ? getShotPath(siteBodyBlock.mobileFileURL)
                : '',
        },
    };

    const handleResetForm = (calb: () => void, values: BannerItemEdit) => {
        calb();
        const equal = isEqualObject(initialValue, values);

        if (!equal) {
            dispatch(
                addNotification({
                    type: TypesSnackbars.ERROR,
                    message: {
                        title: 'Изменения отменены',
                        description: '',
                    },
                    icon: TypesIcon.SNACK_ERROR,
                }),
            );
        }
    };

    return (
        <S.RootWrapper>
            <S.Wrapper>
                <PageTitle>Body сайта</PageTitle>
                <Breadcrumbs breadcrumbs={siteBodyBreadcrumbs()} />
                <Formik<BannerItemEdit>
                    initialValues={initialValue as unknown as BannerItemEdit}
                    onSubmit={handleSubmit}
                    validationSchema={validationSchema}
                    enableReinitialize
                >
                    {props => (
                        <S.StyledForm onSubmit={handleSubmit}>
                            <S.FieldsWrapper>
                                <S.StyledInput
                                    name="title"
                                    onChange={props.handleChange}
                                    value={props.values?.title}
                                    title="Заголовок*"
                                    placeholder="Заголовок*"
                                    typeInput="text"
                                    maxLength={255}
                                    isTouched={props.touched?.title}
                                    error={props.errors?.title}
                                    count={props.submitCount}
                                />

                                <S.StyledInput
                                    name="buttonText"
                                    onChange={props.handleChange}
                                    value={props.values?.buttonText}
                                    title="Текст кнопки"
                                    placeholder="Текст кнопки"
                                    typeInput="text"
                                    maxLength={255}
                                />

                                <S.StyledInput
                                    name="url"
                                    onChange={props.handleChange}
                                    value={props.values?.url}
                                    title="URL*"
                                    placeholder="URL*"
                                    typeInput="text"
                                    isTouched={props.touched.url}
                                    error={props.errors.url}
                                    count={props.submitCount}
                                />
                            </S.FieldsWrapper>
                            <FieldArray name="bannerItems">
                                {() => (
                                    <S.MainWrapper>
                                        {props.values.bannerItems?.map(
                                            (_block: BannerItemForEdit, index: number) => (
                                                // eslint-disable-next-line react/no-array-index-key
                                                <S.ItemWrapper key={`block${index}`}>
                                                    <S.InpurWrapper>
                                                        <S.StyledInput
                                                            name={`bannerItems[${index}].title`}
                                                            onChange={props.handleChange}
                                                            value={props.values?.bannerItems[index]?.title}
                                                            title="Название*"
                                                            placeholder="Название*"
                                                            typeInput="text"
                                                            maxLength={255}
                                                            isTouched={
                                                                props.touched?.bannerItems
                                                                && props.touched?.bannerItems[index]?.title
                                                            }
                                                            error={
                                                                props.errors?.bannerItems
                                                                && props.errors?.bannerItems[index]?.title
                                                            }
                                                            count={props.submitCount}
                                                        />
                                                        <S.StyledInput
                                                            name={`bannerItems[${index}].url`}
                                                            onChange={props.handleChange}
                                                            value={props.values?.bannerItems[index]?.url}
                                                            title="URL*"
                                                            placeholder="URL*"
                                                            typeInput="text"
                                                            maxLength={255}
                                                            isTouched={
                                                                props.touched?.bannerItems
                                                                && props.touched?.bannerItems[index]?.url
                                                            }
                                                            error={
                                                                props.errors?.bannerItems
                                                                && props.errors?.bannerItems[index]?.url
                                                            }
                                                            count={props.submitCount}
                                                        />
                                                    </S.InpurWrapper>

                                                    <S.Uploader
                                                        name={`bannerItems[${index}].fileImage`}
                                                        onChange={props.setFieldValue}
                                                        title="Изображение для desktop версии сайта"
                                                        value={props.values.bannerItems[index].fileImage}
                                                        format="4:4.5"
                                                        maxSize={4}
                                                    />
                                                    <S.Uploader
                                                        name={`bannerItems[${index}].mobileFileImage`}
                                                        onChange={props.setFieldValue}
                                                        title="Изображение для мобильной версии сайта"
                                                        value={
                                                            props.values.bannerItems[index].mobileFileImage
                                                        }
                                                        format="4:4.5"
                                                        maxSize={4}
                                                    />
                                                </S.ItemWrapper>
                                            ),
                                        )}
                                    </S.MainWrapper>
                                )}
                            </FieldArray>
                            <S.ButtonsWrapper>
                                <SubmitButtons
                                    reset={() => handleResetForm(props.resetForm, props.values)}
                                    submit={props.submitForm}
                                />
                            </S.ButtonsWrapper>
                        </S.StyledForm>
                    )}
                </Formik>
            </S.Wrapper>
        </S.RootWrapper>
    );
};

const loader = createLoadHoc({
    selectLoader: selectGetSiteBlocksLoader,
});

export const OurServicesWithLoader = loader(OurServicesContent);
